/**
 * Paid-launch offer helpers (card A1).
 *
 * Pure, presentation-only logic for the RC-Offerings-driven Spending Tools
 * paywall: which offering to read, how to describe the annual saving, and how
 * to word the trial terms honestly. No prices are hard-coded here — everything
 * is computed from whatever the RevenueCat Offering returns (§5 of
 * PAID_LAUNCH_PLAN.md: "Retire the LEAD_PRICE = 10 constant — price comes from
 * the Offering").
 */

/**
 * The RevenueCat Offering the paid-launch paywall reads, BY IDENTIFIER — not
 * `.current`. Keeping `default` as the Current offering is what guarantees the
 * new packages never surface in the app before we deliberately flip the
 * `paid_launch_enabled` flag (PAID_LAUNCH_PLAN.md §10, reversible cutover).
 *
 * NOTE: the identifier contains a space — this is the single source of truth
 * for that string, so no other code ever re-types it. (RC offering identifiers
 * are immutable; recreating for cosmetics wasn't worth rebuilding the packages.)
 */
export const PAID_OFFERING_ID = 'spending tools';

/**
 * Web (Stripe) price source for the paid-launch offer (card T2). RC Offerings
 * don't exist on web, so the web paywall reads these two env-configured price
 * IDs directly instead. Degrade gracefully: if either is absent, the web
 * branch never renders — `webPricesConfigured()` gates it and PaywallModal
 * falls back to the legacy Stripe paywall rather than risk a broken checkout.
 *
 * New env vars (document in deploy config before flipping `paid_launch_enabled`
 * on web):
 * - VITE_STRIPE_SPENDING_MONTHLY_PRICE_ID
 * - VITE_STRIPE_SPENDING_ANNUAL_PRICE_ID
 */
export const WEB_PRICES = {
  monthly: { priceId: import.meta.env.VITE_STRIPE_SPENDING_MONTHLY_PRICE_ID as string | undefined, amount: 15 },
  annual: { priceId: import.meta.env.VITE_STRIPE_SPENDING_ANNUAL_PRICE_ID as string | undefined, amount: 100 },
};

/** True when both web (Stripe) price env vars are configured — gates the web branch of the paywall. */
export function webPricesConfigured(): boolean {
  return !!WEB_PRICES.monthly.priceId && !!WEB_PRICES.annual.priceId;
}

/**
 * Hosted legal documents for pre-login surfaces (the native paywall screen
 * must link Terms + Privacy per App Store subscription rules, and pre-login
 * there's no authed /legal route to send people to). Same Termly documents
 * LegalView embeds — keep the UUIDs in sync with LegalView.tsx.
 */
export const TERMS_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=4226b670-201c-4762-a27f-cff13fa6a306';
export const PRIVACY_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=a95796cb-2a01-4b78-b49f-42953c9b49f1';

/**
 * How many days before a trial ends we promise to remind the user. The paywall
 * makes this promise in copy ("we'll remind you N days before"), so it is a
 * HARD DEPENDENCY: a real trial-ending reminder notification must be sent at
 * this lead time (owned by the trial-expiry / dunning work, card A3 +
 * notifications) before `paid_launch_enabled` is flipped on. If we can't send
 * it, remove the promise from the copy rather than break trust.
 */
export const TRIAL_END_REMINDER_DAYS = 2;

/**
 * A normalized free-trial descriptor attached to a package (store-agnostic).
 *
 * TRIAL-LENGTH COPY POLICY (Justin, 2026-07-16): the stores don't offer the
 * same units, so we don't pretend they do —
 * - **Native says "1 month".** Apple only offers month-unit intro offers
 *   ("1 month", 28–31 days, renews same day next month) and its payment sheet
 *   says "1 month free" regardless of our copy. Play is deliberately
 *   configured to 1 month too (P1M — note the Play OFFER IDS still read
 *   "…-30-day-trial"; the ID is immutable and wrong, the duration is 1 month).
 * - **Web says "30 days".** Stripe's trial is exactly `trial_period_days=30`.
 *
 * All trial-length copy must come from `periodLabel` (derived from the store
 * product on native, asserted as "30 days" on web) — NEVER hardcode a trial
 * length in a component, or iOS copy will contradict Apple's own payment
 * sheet at the moment of purchase. See docs/operations/2026/Q3/IOS_TRIAL_OFFERS.md.
 */
export interface TrialDescriptor {
  /** Length of the free trial, e.g. "30 days" / "1 month" — already localized-ish. */
  periodLabel: string;
  /** Approx trial length in days when resolvable (for "billed on <date>" math), else null. */
  approxDays: number | null;
  /** Store period unit, when known — lets date math be calendar-exact for month/year trials. */
  unit?: 'day' | 'week' | 'month' | 'year';
  /** Number of `unit`s in the trial, when known. */
  count?: number;
}

/**
 * The web (Stripe) trial descriptor — exactly `trial_period_days=30`, and
 * deliberately NOT "1 month" (that's the native wording, per the copy policy
 * above). Stripe exposes no per-product trial metadata to read, so unlike the
 * native stores this is asserted rather than derived — which is safe only
 * because `freeTrialUsed` + `enough_check_trial_eligibility` gate whether the
 * copy renders at all on web.
 */
export const WEB_TRIAL: TrialDescriptor = {
  periodLabel: '30 days',
  approxDays: 30,
  unit: 'day',
  count: 30,
};

/**
 * Whole-number percent saved by paying annually vs 12× the monthly price.
 * Returns null when either price is missing or non-positive (degrade: show no
 * savings badge rather than a bogus one).
 *
 * e.g. monthly $15, annual $100 → round(1 - 100/180) = 44.
 */
export function annualSavingsPercent(
  monthlyPrice: number | null | undefined,
  annualPrice: number | null | undefined,
): number | null {
  if (!monthlyPrice || !annualPrice || monthlyPrice <= 0 || annualPrice <= 0) return null;
  const yearlyAtMonthly = monthlyPrice * 12;
  if (annualPrice >= yearlyAtMonthly) return null; // no saving to advertise
  return Math.round((1 - annualPrice / yearlyAtMonthly) * 100);
}

/**
 * The effective monthly cost of the annual plan, for "$X.XX/mo billed
 * annually" copy. Null when the annual price is missing.
 */
export function annualMonthlyEquivalent(annualPrice: number | null | undefined): number | null {
  if (!annualPrice || annualPrice <= 0) return null;
  return annualPrice / 12;
}

/**
 * "$15" or "$8.33" — whole-dollar amounts drop the cents, everything else
 * keeps exactly two decimal places. Paywall copy policy: never "$15.00".
 */
export function formatUsd(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

/**
 * Drop ".00" / ",00" cents from a store-formatted price string when the
 * amount is whole ("$15.00" → "$15", "15,00 €" → "15 €"). The stores always
 * render two decimals; our paywall copy policy says whole dollars show none.
 * Non-whole prices ("$15.50") and digit-grouping separators ("$1,000.00")
 * are left intact — the pattern only matches a trailing zero-cents part.
 */
export function stripWholeDollarCents(priceString: string): string {
  return priceString.replace(/([.,])00(?!\d)/, '');
}

/**
 * Honest trial line. We must state BOTH what the user is charged and WHEN
 * (PAID_LAUNCH_PLAN.md §4a: "render trial terms honestly: what you're charged,
 * and when"). No dark-pattern vagueness.
 *
 * With a resolvable trial length and today's date we name the billing date;
 * otherwise we fall back to "when your free trial ends".
 */
export function formatTrialTerms(
  priceString: string,
  periodSuffix: string,
  trial: TrialDescriptor | null,
  now?: Date,
): string {
  if (!trial) {
    // Trial-used (or no trial on this package): price-only, no trial language.
    return `${priceString}${periodSuffix}`;
  }
  const billed = now ? trialEndDate(now, trial) : null;
  const when = billed
    ? `on ${billed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : `when your ${trial.periodLabel} free trial ends`;
  // The cancel reassurance lives in the paywall footer ("Cancel anytime from
  // your profile") — this line only states the charge: what and when.
  return `Free for ${trial.periodLabel}, then ${priceString}${periodSuffix} ${when}.`;
}

/**
 * The first billing date for a trial started at `from`, or null when the
 * descriptor can't resolve one.
 *
 * Month/year trials use CALENDAR math, matching how the stores actually bill:
 * Apple's "1 month" renews on the same day next month, clamped to month end
 * (Jul 16 → Aug 16; Jan 31 → Feb 28) — 28 to 31 days, not 30. Adding
 * `approxDays` there names a date a day or two off Apple's real charge, and a
 * wrong exact date is worse than no date. Day/week (and descriptors with no
 * unit) fall back to `approxDays`.
 */
export function trialEndDate(from: Date, trial: TrialDescriptor): Date | null {
  const d = new Date(from.getTime());
  if (trial.unit === 'month' || trial.unit === 'year') {
    const months = (trial.count ?? 1) * (trial.unit === 'year' ? 12 : 1);
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, lastDay));
    return d;
  }
  if (trial.approxDays == null) return null;
  d.setDate(d.getDate() + trial.approxDays);
  return d;
}

/** "Aug 14" style label for the first billing date, `days` after `from`. */
export function billingDateLabel(from: Date, days: number): string {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Orphaned-purchase hint (device-local, native only).
 *
 * The pre-login stranded-payer check (AuthGuard) learns that THIS DEVICE's
 * anonymous RevenueCat user holds an active entitlement. If the visitor then
 * signs IN to an existing account instead of creating one, the purchase stays
 * behind on the anonymous uid — and the freshly-authed account gets walled by
 * PaidAccessGate. This hint carries the fact across the auth transition so
 * the gate can lead with "Restore purchases" instead of a fresh sell.
 *
 * ROLE DEMOTED (2026-07-22): the StoreEntitlements plugin now asks the
 * device's STORE ACCOUNT directly (StoreKit 2 currentEntitlements / Play
 * Billing queryPurchasesAsync) — ground truth that survives reinstall and
 * cleared storage, which this breadcrumb never could. The hint is kept for
 * exactly two jobs: (1) the INSTANT signal at gate mount while the store
 * query is in flight, and (2) the fallback when the query can't answer
 * (billing service unavailable). When the query answers authoritatively, it
 * outranks the hint in both directions — an owned sub leads with Restore
 * hint or no hint, and an empty answer clears a stale hint (PaidAccessGate).
 *
 * Deliberately a HINT, never a grant: restore is receipt-verified by the
 * store, so a wrong or stale hint can only change copy — it can never move an
 * entitlement. Device-local is also the correct scope: restore only works on
 * the device that owns the store account, so a server-side link would add
 * nothing actionable here.
 *
 * ONLY stamp for a restorable entitlement (`isRestorableStoreForPlatform`):
 * "Restore purchases" replays THIS device's store receipts, so it can only
 * ever surface a purchase made in the store this device talks to (Apple
 * purchase ↔ iOS, Play purchase ↔ Android). A PROMOTIONAL (RC-granted) or
 * STRIPE entitlement on the anon uid has nothing to restore natively — a
 * hint for one would promise a membership the Restore button can't find.
 */
const ORPHANED_PURCHASE_HINT_KEY = 'enough_orphaned_purchase_hint';

/**
 * RevenueCat entitlement stores whose purchases the native "Restore
 * purchases" flow can actually recover, per Capacitor platform. Everything
 * absent here (PROMOTIONAL, STRIPE, RC_BILLING, …) restores to nothing on
 * device — those entitlements move to an account via uid-preserving signup
 * or the RC transfer on login, never via the Restore button.
 */
const RESTORABLE_STORES_BY_PLATFORM: Record<string, readonly string[]> = {
  ios: ['APP_STORE', 'MAC_APP_STORE'],
  android: ['PLAY_STORE'],
};

/** True when an entitlement from `store` is recoverable by the native Restore flow on `platform`. */
export function isRestorableStoreForPlatform(
  store: string | null | undefined,
  platform: string,
): boolean {
  if (!store) return false;
  return (RESTORABLE_STORES_BY_PLATFORM[platform] ?? []).includes(store);
}

export function markOrphanedPurchaseHint(anonUid: string, store: string): void {
  try {
    localStorage.setItem(
      ORPHANED_PURCHASE_HINT_KEY,
      JSON.stringify({ anonUid, store, at: new Date().toISOString() }),
    );
  } catch { /* storage unavailable — hint is best-effort */ }
}

export function readOrphanedPurchaseHint(): { anonUid: string; store?: string; at: string } | null {
  try {
    const raw = localStorage.getItem(ORPHANED_PURCHASE_HINT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearOrphanedPurchaseHint(): void {
  try {
    localStorage.removeItem(ORPHANED_PURCHASE_HINT_KEY);
  } catch { /* ignore */ }
}
